import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';

/**
 * FS-889 (Plan 132) — saved Device List reports.
 *
 *   GET    /devices/reports/list            every report the caller can see
 *   GET    /devices/reports/share-scopes    the paths the caller may share to
 *   GET    /devices/reports/:id             one report with its state
 *   POST   /devices/reports/new             create (owner = the caller)
 *   PATCH  /devices/reports/:id             rename / save state / share / unshare
 *   DELETE /devices/reports/:id             hard delete (the audit row stays)
 *
 * Mounted under /devices so the existing CloudFront route reaches it (the same
 * reason the tags live at /devices/tags). Every route needs `read_devices`;
 * what the caller may do to one report comes back as its `can_*` flags, which
 * are the only rights the client reads. Every call takes an AbortSignal so a
 * response that lands after the page moved on can be dropped.
 */

const reportsUrl = () => `${config.devicesApiUrl}/devices/reports`;

export const REPORT_PAGE_SIZES = [20, 50, 100, 200] as const;
export type ReportPageSize = (typeof REPORT_PAGE_SIZES)[number];

/** The saved view, exactly as stored in `device_list_reports.state`. */
export interface ReportStateV1 {
  v: 1;
  columnOrder: string[];
  hidden: string[];
  widths: Record<string, number>;
  filters: Record<string, string>;
  sort: string;
  pageSize: ReportPageSize;
  q: string;
}

export interface ReportSummary {
  id: number;
  name: string;
  /** null = private; otherwise the scope path it is shared at. */
  shared_scope: string | null;
  owner_user_id: number | null;
  /** null when the owner's account was deleted. */
  owner_email: string | null;
  is_mine: boolean;
  created_at: string;
  /** The concurrency token every write sends back as `expected_updated_at`. */
  updated_at: string;
  can_edit: boolean;
  can_share: boolean;
  can_unshare: boolean;
  can_delete: boolean;
}

export interface Report extends ReportSummary {
  state: ReportStateV1;
}

export interface ShareScope {
  path: string;
  /** Accounts whose device scope covers the path (stored grants, not live tokens). */
  account_count: number;
}

export interface CreateReportBody {
  name: string;
  state: ReportStateV1;
  shared_scope: string | null;
  confirm_share?: true;
}

export interface UpdateReportBody {
  expected_updated_at: string;
  name?: string;
  state?: ReportStateV1;
  shared_scope?: string | null;
  confirm_share?: true;
}

/** Error codes the server names in `{ status, code, message }` bodies. */
export type ReportErrorCode =
  | 'invalid'
  | 'outside_share_scope'
  | 'account_missing'
  | 'forbidden'
  | 'not_found'
  | 'stale'
  | 'name_taken'
  | 'share_unconfirmed';

export async function listReports(signal: AbortSignal): Promise<ReportSummary[]> {
  const res = await axiosInstance.get<{ reports: ReportSummary[] }>(`${reportsUrl()}/list`, { signal });
  return res.data.reports;
}

export async function fetchShareScopes(signal: AbortSignal, path?: string): Promise<ShareScope[]> {
  const res = await axiosInstance.get<{ scopes: ShareScope[] }>(`${reportsUrl()}/share-scopes`, {
    signal,
    ...(path !== undefined ? { params: { path } } : {}),
  });
  return res.data.scopes;
}

export async function fetchReport(id: number, signal: AbortSignal): Promise<Report> {
  const res = await axiosInstance.get<Report>(`${reportsUrl()}/${id}`, { signal });
  return res.data;
}

export async function createReport(body: CreateReportBody, signal: AbortSignal): Promise<Report> {
  const res = await axiosInstance.post<Report>(`${reportsUrl()}/new`, body, { signal });
  return res.data;
}

/**
 * `report` is null when the caller can no longer see the result — an admin
 * unsharing someone else's report takes it out of their own view.
 */
export async function updateReport(
  id: number,
  body: UpdateReportBody,
  signal: AbortSignal,
): Promise<{ report: Report | null }> {
  const res = await axiosInstance.patch<{ report: Report | null }>(`${reportsUrl()}/${id}`, body, { signal });
  return res.data;
}

export async function deleteReport(
  id: number,
  expectedUpdatedAt: string,
  signal: AbortSignal,
): Promise<{ id: number; deleted: true }> {
  const res = await axiosInstance.delete<{ id: number; deleted: true }>(`${reportsUrl()}/${id}`, {
    signal,
    params: { expected_updated_at: expectedUpdatedAt },
  });
  return res.data;
}

/** The server's error `code` from a failed call, or null when there is none. */
export function reportErrorCode(err: unknown): ReportErrorCode | string | null {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return null;
}

/** The HTTP status of a failed call, or undefined for a network error or timeout. */
export function reportErrorStatus(err: unknown): number | undefined {
  const status = (err as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** The extra `filter` field an `outside_share_scope` refusal names. */
export function reportErrorFilter(err: unknown): string | null {
  const data = (err as { response?: { data?: { filter?: unknown } } } | null)?.response?.data;
  return typeof data?.filter === 'string' ? data.filter : null;
}

/** True when the request was cancelled by its AbortSignal. */
export function isAbortError(err: unknown): boolean {
  const e = err as { code?: unknown; name?: unknown } | null;
  return e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';
}
